import './user-card-form.scss';
import {useEffect, useState} from 'react';
import {useOffice} from '../../contexts/office/context';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {UserCard} from '../../backend/interfaces/user-card';
import {UsersRepository} from '../../repositories/users';
import {EntitiesRepository} from '../../repositories/entities';
import {Entity} from '../../backend/interfaces/entity';
import {LoadingContent} from '../loading-content/loading-content';
import {Alert, Box, Button, Divider, Stack, Typography} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {
  FlatSearchSelectInput,
  SearchSelectOption,
} from '../flat-search-select-input/flat-search-select-input';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {UserCardsRepository} from '../../repositories/user-cards';
import {ScreenPaths} from '../../enums/screen-path';
import {PostUserCard} from '../../backend/requests/post-user-card';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {PutUserCard} from '../../backend/requests/put-user-card';
import {User} from '../../backend/interfaces/user';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import {CaregiverMode} from '../../enums';
import GenericHeader from '../generic-header/generic-header';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {Modal} from '../modal/modal';

interface SearchSelectInputItem {
  id: string;
  name: string;
  picture?: string;
}

/**
 * Form to view, edit, remove, and create user cards.
 *
 * It uses the office context to get the user card's information.
 *
 * Permissions are given by props.
 */
export const UserCardForm = (props: {
  redirectUrl: string;
  showPageTitle: boolean;
  showExitButton: boolean;
  caregiverMode: CaregiverMode;
  canWrite: boolean;
  titleCrumbs?: Array<{label: string; link: string}>;
}) => {
  const {t} = useTranslation();
  let {url} = useRouteMatch();
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const [originalModel, setOriginalModel] = useState<UserCard | undefined>();
  const [model, setModel] = useState<UserCard | undefined>();
  const [user, setUser] = useState<SearchSelectInputItem | undefined>();
  const [entity, setEntity] = useState<SearchSelectInputItem | undefined>();
  const [tag, setTag] = useState<string>('');
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string | undefined>();
  const [showNameError, setShowNameError] = useState<boolean>(false);
  const [tagError, setTagError] = useState<string | undefined>();
  const [showTagError, setShowTagError] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(false);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);

  let history = useHistory();

  const isSubmitting =
    officeContext.state.userCardFormSubmitButtonState === SubmitButtonState.Loading;
  const isDeleting =
    officeContext.state.userCardFormDeleteButtonState === SubmitButtonState.Loading;

  // Set model when we get one
  useEffect(() => {
    // Set the model
    const init = async () => {
      if (officeContext.state.userCardForm) {
        // Init buttons state
        officeContext.setUserCardFormSubmitButtonState(SubmitButtonState.Normal);
        officeContext.setUserCardFormDeleteButtonState(SubmitButtonState.Normal);

        // Init some state
        setDataChanged(false);

        try {
          // Get the user
          if (officeContext.state.userCardForm.userId) {
            const userResult: User = await UsersRepository.instance.getById(
              officeContext.state.userCardForm.userId
            );

            if (userResult && userResult.id) {
              // Set user
              setUser({
                id: userResult.id,
                name: userResult.name,
                picture: userResult.picture,
              });
            } else {
              // Error
              throw new Error('Empty user.');
            }
          }

          // Get the entity
          if (officeContext.state.userCardForm.entityId) {
            const entityResult: Entity = await EntitiesRepository.instance.getById(
              officeContext.state.userCardForm.entityId
            );

            if (entityResult && entityResult.id) {
              // Set entity
              setEntity({
                id: entityResult.id,
                name: entityResult.name,
                picture: entityResult.picture,
              });
            } else {
              // Error
              throw new Error('Empty entity.');
            }
          }

          // Set models
          setModel(officeContext.state.userCardForm);
          setOriginalModel(officeContext.state.userCardForm);

          // Clean tag
          setTag('');
        } catch (error) {
          // Log
          console.debug('🔴 Could not open the user card.', error);

          // Redirect
          history.push(props.redirectUrl);
        }
      }
    };

    // Init
    init();

    // Clean up
    return () => {
      // Log
      console.debug('🧹 User card form button states cleaned in office context.');

      // Clear button states
      officeContext.clearUserCardFormSubmitButtonState();
      officeContext.clearUserCardFormDeleteButtonState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.userCardForm]);

  // Form validation
  useEffect(() => {
    if (model && originalModel && props.canWrite) {
      // Hold validation
      let nameValid: boolean = false;
      let tagValid: boolean = false;

      // Name validation
      if (model.name && model.name.trim().length > 0) {
        // No errors
        if (nameError !== undefined) {
          setNameError(undefined);
        }

        // Valid
        nameValid = true;
      } else {
        // Error
        if (showNameError) {
          setNameError(
            model.id
              ? t(translations.user_card_form.errors.edit_form_validation_invalid_field)
              : t(translations.user_card_form.errors.create_form_validation_invalid_field)
          );
        }

        // Valid
        nameValid = false;
      }

      // Tag validation
      if (model.id) {
        // Valid
        tagValid = true;
      } else {
        if (tag && tag.trim().length > 0) {
          // No errors
          if (tagError !== undefined) {
            setTagError(undefined);
          }

          // Valid
          tagValid = true;
        } else {
          // Error
          if (showTagError) {
            setTagError(t(translations.user_card_form.errors.create_form_validation_invalid_field));
          }

          // Valid
          tagValid = false;
        }
      }

      // Data was updated
      let dataWasUpdated: boolean = false;

      // Check if the data was changed for the router prompt and the buttons validation
      if (
        (model.name || '').trim() !== (originalModel.name || '').trim() ||
        model.isActive !== originalModel.isActive ||
        user?.id !== originalModel.userId ||
        entity?.id !== originalModel.entityId ||
        tag
      ) {
        // Set it
        setDataChanged(true);

        // Set
        dataWasUpdated = true;
      } else {
        // Set it
        setDataChanged(false);

        // Set
        dataWasUpdated = false;
      }

      // Submit button validation
      if (model.id && props.canWrite) {
        if (nameValid && dataWasUpdated) {
          // Enable submit button
          setSubmitButtonDisabled(false);
        } else {
          // Disable submit button
          setSubmitButtonDisabled(true);
        }
      } else if (props.caregiverMode === CaregiverMode.SystemAdministrator && !model.id) {
        if (nameValid && tagValid) {
          // Enable submit button
          setSubmitButtonDisabled(false);
        } else {
          // Disable submit button
          setSubmitButtonDisabled(true);
        }
      } else {
        // Disable submit button
        setSubmitButtonDisabled(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model,
    originalModel,
    tag,
    user,
    entity,
    showNameError,
    showTagError,
    nameError,
    tagError,
    props.canWrite,
    props.caregiverMode,
  ]);

  // Submit
  const handleSaveChanges = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || !props.canWrite) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setUserCardFormSubmitButtonState(SubmitButtonState.Loading);

      // Disabled delete button
      setDeleteButtonDisabled(true);

      // Create or edit
      if (!model.id && props.caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Create <---

        // Request body
        const requestBody: PostUserCard = {
          isActive: model.isActive,
          tagData: tag.trim(),
          name: model.name?.trim() || undefined,
          userId: user?.id || undefined,
          entityId: entity?.id || undefined,
        };

        // Create
        const response = await UserCardsRepository.instance.create(requestBody);

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to edit mode
          history.replace(
            props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
              ? `${props.redirectUrl}${response.id}`
              : `${props.redirectUrl}/${response.id}`
          );

          // Reset context
          officeContext.setUserCardForm(response);

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.user_card_form.success.create_success_message, {
              name: response.name,
            }),
          });

          // Update submit button state
          officeContext.setUserCardFormSubmitButtonState(SubmitButtonState.Success);

          // Enable delete button
          setDeleteButtonDisabled(false);
        }, 500);
      } else if (model.id && props.caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Edit <---

        // Request body
        const requestBody: PutUserCard = {
          isActive: model.isActive,
          name: model.name?.trim() || null,
          userId: user?.id || null,
          entityId: entity?.id || null,
        };

        // Edit
        const response = await UserCardsRepository.instance.updateById(model.id, requestBody);

        // Reset context
        officeContext.setUserCardForm(response);

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.user_card_form.success.edit_success_message),
        });

        // Update submit button state
        officeContext.setUserCardFormSubmitButtonState(SubmitButtonState.Success);

        // Enable delete button
        setDeleteButtonDisabled(false);
      } else if (model.id && props.canWrite) {
        // ---> Edit <---

        // Request body
        const requestBody: PutUserCard = {
          isActive: model.isActive,
          name: model.name?.trim() || null,
          userId: user?.id || null,
          // The entityId can't be updated
          entityId: undefined,
        };

        // Edit
        const response = await UserCardsRepository.instance.updateById(model.id, requestBody);

        // Reset context
        officeContext.setUserCardForm(response);

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.user_card_form.success.edit_success_message),
        });

        // Update submit button state
        officeContext.setUserCardFormSubmitButtonState(SubmitButtonState.Success);

        // Enable delete button
        setDeleteButtonDisabled(false);
      } else {
        // Throw error
        throw new Error('Missing permissions.');
      }
    } catch (error) {
      // Log
      console.debug('🔴 Could not edit/create the user card.', error);

      // Update submit button state
      officeContext.setUserCardFormSubmitButtonState(SubmitButtonState.Error);

      // Enable delete button
      setDeleteButtonDisabled(false);
    }
  };

  // Delete the card
  const handleDeleteUserCard = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || props.caregiverMode !== CaregiverMode.SystemAdministrator) {
      return;
    }

    try {
      // Close dialog
      setDeleteDialogOpened(false);

      // Disable submit button
      setSubmitButtonDisabled(true);

      // Update delete button state
      officeContext.setUserCardFormDeleteButtonState(SubmitButtonState.Loading);

      // Delete the user card
      if (originalModel.id) {
        await UserCardsRepository.instance.deleteById(originalModel.id);
      } else {
        // Error
        throw new Error('Empty user card id.');
      }

      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated
      setTimeout(() => {
        // Navigate back
        history.replace(
          props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
            ? `${props.redirectUrl}?${ScreenPaths.UserCardsDeletedQuery}=${originalModel?.name}`
            : `${props.redirectUrl}/?${ScreenPaths.UserCardsDeletedQuery}=${originalModel?.name}`
        );
      }, 500);
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not delete the user card.', error);

      // Update delete button state
      officeContext.setUserCardFormDeleteButtonState(SubmitButtonState.Error);

      // Enable submit button again
      setSubmitButtonDisabled(false);
    }
  };

  // Change the name
  const handleChangeName = (value: string | undefined | null) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setModel({
      ...model,
      name: finalValue,
    } as UserCard);
  };

  // Change the tag
  const handleChangeTag = (value: string | undefined | null) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setTag(finalValue);
  };

  // Change the entity
  const handleChangeEntity = (value?: SearchSelectOption<SearchSelectInputItem>) => {
    if (!value || (value && value.id !== entity?.id)) {
      // Clear the user
      setUser(undefined);
    }

    // Set the entity
    setEntity(value ? value.item : undefined);
    setModel({
      ...model,
      entityId: value?.id,
    } as UserCard);
  };

  // Fetch entities
  const handleFetchEntities = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{data: Array<SearchSelectOption<SearchSelectInputItem>>; total: number}> => {
    // Get entities
    const response = await EntitiesRepository.instance.get(skip, limit, search);

    // Return
    return {
      data: response.data.map((obj) => {
        return {
          id: obj.id || '',
          name: obj.name || '',
          item: {id: obj.id || '', name: obj.name || '', picture: obj.picture},
          avatar: obj.picture,
        };
      }),
      total: response.totalCount,
    };
  };

  // Change the user
  const handleChangeUser = (value?: SearchSelectOption<SearchSelectInputItem>) => {
    setUser(value ? value.item : undefined);
    setModel({
      ...model,
      userId: value?.id,
    } as UserCard);
  };

  // Fetch users
  const handleFetchUsers = async (
    limit: number,
    skip: number,
    search?: string
  ): Promise<{data: Array<SearchSelectOption<SearchSelectInputItem>>; total: number}> => {
    if (entity) {
      // Get users: seniors and professional caregivers
      const response = await UsersRepository.instance.getInEntity(
        entity.id,
        limit,
        skip,
        undefined,
        search,
        undefined,
        props.caregiverMode === CaregiverMode.SystemAdministrator
          ? undefined
          : 'activationStates=true'
      );

      // Return
      return {
        data: response.data.map((obj) => {
          return {
            id: obj.user.id || '',
            name: obj.user.name || '',
            item: {id: obj.user.id || '', name: obj.user.name || '', picture: obj.user.picture},
            avatar: obj.user.picture,
          };
        }),
        total: response.totalCount,
      };
    } else {
      // Log
      console.debug('ℹ️ No entity is set to fetch users.');

      // Return
      return {
        data: [],
        total: 0,
      };
    }
  };

  // Strings to use in the form
  let routerDialogTitle: string = '';
  let routerDialogContent: string = '';
  let routerCancelToast: string = '';

  if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
    routerDialogTitle = model?.id
      ? t(translations.user_card_form.router_prompt_edit_title, {
          name: originalModel?.name || '',
        })
      : t(translations.user_card_form.router_prompt_create_title, {name: model?.name || ''});
    routerDialogContent = model?.id
      ? t(translations.user_card_form.router_prompt_edit_message)
      : t(translations.user_card_form.router_prompt_create_message);
    routerCancelToast = model?.id
      ? t(translations.user_card_form.edit_cancel_toast, {
          name: originalModel?.name || '',
        })
      : t(translations.user_card_form.create_cancel_toast);
  } else {
    routerDialogTitle = t(translations.user_card_form.router_prompt_edit_title, {
      name: originalModel?.name || '',
    });
    routerDialogContent = t(translations.user_card_form.router_prompt_edit_message);
    routerCancelToast = t(translations.user_card_form.edit_cancel_toast, {
      name: originalModel?.name || '',
    });
  }

  return (
    <>
      {props.titleCrumbs && (
        <GenericHeader
          isLoading={false}
          titleCrumbs={[
            ...props.titleCrumbs,
            {
              label: model?.name || t(translations.user_card_form.create_user_card_title),
              link: url,
            },
          ]}
          leftColumn={null}
          rightColumn={null}
        />
      )}
      <Box className="user-card-form-container">
        <Box className="listing-container-centered">
          {model ? (
            <form>
              <Box className="user-card-form-line-1-container">
                <FlatInput
                  className="user-card-form-line-1-left"
                  label={t(translations.user_card_form.name_input)}
                  value={model.name || ''}
                  onChange={(e) => handleChangeName(e.currentTarget.value)}
                  onBlur={() => {
                    if (!showNameError) {
                      setShowNameError(true);
                    }
                  }}
                  errors={nameError}
                  disabled={
                    props.caregiverMode !== CaregiverMode.SystemAdministrator ||
                    officeContext.state.userCardFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.userCardFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  required={true}
                  type="text"
                />
                <Box className="user-card-form-line-1-right">
                  <FlatSelectDropdown
                    label={t(translations.user_card_form.state_input)}
                    value={{
                      key: model.isActive.toString(),
                      label: model.isActive
                        ? t(translations.user_card_form.state_value_true)
                        : t(translations.user_card_form.state_value_false),
                    }}
                    onChange={(e) =>
                      setModel({
                        ...model,
                        isActive: JSON.parse(e.key),
                      })
                    }
                    required={true}
                    disabled={
                      props.caregiverMode !== CaregiverMode.SystemAdministrator ||
                      !props.canWrite ||
                      officeContext.state.userCardFormSubmitButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.userCardFormDeleteButtonState ===
                        SubmitButtonState.Loading
                    }
                    disabledTooltip={
                      !props.canWrite ? t(translations.user_card_form.disabled_tooltip) : undefined
                    }
                    options={[
                      {key: 'true', label: t(translations.user_card_form.state_value_true)},
                      {key: 'false', label: t(translations.user_card_form.state_value_false)},
                    ]}
                  />
                </Box>
              </Box>
              {props.caregiverMode === CaregiverMode.SystemAdministrator && !model.id && (
                <Box className="user-card-form-line-2-container">
                  <FlatInput
                    className="user-card-form-line-2"
                    label={t(translations.user_card_form.tag_input)}
                    value={tag}
                    onChange={(e) => handleChangeTag(e.currentTarget.value)}
                    onBlur={() => {
                      if (!showTagError) {
                        setShowTagError(true);
                      }
                    }}
                    errors={tagError}
                    disabled={
                      officeContext.state.userCardFormSubmitButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.userCardFormDeleteButtonState ===
                        SubmitButtonState.Loading
                    }
                    required={true}
                    type="text"
                  />
                </Box>
              )}
              {props.caregiverMode === CaregiverMode.SystemAdministrator && (
                <Box className="user-card-form-line-3-container">
                  <FlatSearchSelectInput<SearchSelectInputItem>
                    label={t(translations.user_card_form.entity_input)}
                    value={
                      entity
                        ? {
                            id: entity.id,
                            name: entity.name,
                            item: entity,
                            avatar: entity.picture,
                          }
                        : undefined
                    }
                    handleChange={handleChangeEntity}
                    handleFetchData={handleFetchEntities}
                    fetchWhenEmpty={true}
                    disabled={
                      officeContext.state.userCardFormSubmitButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.userCardFormDeleteButtonState ===
                        SubmitButtonState.Loading
                    }
                    avatar={{isIcon: false, type: 'entity'}}
                  />
                </Box>
              )}
              <Box className="user-card-form-line-4-container">
                <FlatSearchSelectInput<SearchSelectInputItem>
                  className="user-card-form-line-4"
                  label={t(translations.user_card_form.user_input)}
                  value={
                    user
                      ? {
                          id: user.id,
                          name: user.name,
                          item: user,
                          avatar: user.picture,
                        }
                      : undefined
                  }
                  handleChange={handleChangeUser}
                  handleFetchData={handleFetchUsers}
                  fetchWhenEmpty={true}
                  disabled={
                    !props.canWrite ||
                    officeContext.state.userCardFormSubmitButtonState ===
                      SubmitButtonState.Loading ||
                    officeContext.state.userCardFormDeleteButtonState === SubmitButtonState.Loading
                  }
                  notAllowedEditing={!props.canWrite}
                  notAllowedEditingTooltipTitle={t(translations.user_card_form.disabled_tooltip)}
                  avatar={{isIcon: false, type: 'user'}}
                />
              </Box>
              {props.caregiverMode === CaregiverMode.SystemAdministrator && (
                <Alert severity="info" className="user-card-form-user-alert">
                  {t(translations.user_card_form.user_info_message)}
                </Alert>
              )}
              {props.caregiverMode === CaregiverMode.Professional && (
                <Stack
                  className="user-card-form-footer"
                  flexDirection="row"
                  gap="10px"
                  justifyContent="space-between"
                >
                  <Button
                    variant="text"
                    color="error"
                    sx={{
                      width: '100%',
                      height: '40px',
                      padding: '8px 16px',
                      borderRadius: '5px',
                      maxWidth: '252px',
                    }}
                    onClick={() => {
                      history.push(props.redirectUrl);
                    }}
                    disabled={isSubmitting || isDeleting}
                  >
                    {t(translations.user_form.cancel_button)}
                  </Button>
                  <LoadingButton
                    sx={{
                      width: '100%',
                      maxWidth: '252px',
                    }}
                    type="button"
                    variant="contained"
                    loading={isSubmitting}
                    loadingPosition={isSubmitting ? 'start' : undefined}
                    disabled={!props.canWrite || submitButtonDisabled || isSubmitting || isDeleting}
                    onClick={handleSaveChanges}
                  >
                    {t(translations.user_card_form.save_button)}
                  </LoadingButton>
                </Stack>
              )}
            </form>
          ) : (
            <LoadingContent />
          )}
          {/* Delete button and dialog */}
          {props.caregiverMode === CaregiverMode.SystemAdministrator && (
            <>
              <Box className="user-card-form-delete-container">
                <Divider></Divider>
                <Box className="user-card-form-delete-button-container">
                  {model && model.id && (
                    <SubmitButton
                      disabled={deleteButtonDisabled}
                      state={
                        officeContext.state.userCardFormDeleteButtonState ||
                        SubmitButtonState.Normal
                      }
                      onStateChange={(newState: SubmitButtonState) => {
                        officeContext.setUserCardFormDeleteButtonState(newState);
                      }}
                      normalStateLabel={t(translations.user_card_form.delete_button)}
                      loadingStateLabel={t(translations.user_card_form.deleting_button)}
                      successStateLabel={t(
                        translations.user_card_form.success.delete_success_button
                      )}
                      errorStateLabel={t(translations.user_card_form.errors.error_on_delete_button)}
                      endIcon={<DeleteIcon />}
                      type="button"
                      variant="outlined"
                      color="error"
                      onClick={(event: any) => {
                        event.preventDefault();

                        setDeleteDialogOpened(true);
                      }}
                    />
                  )}
                  <SubmitButton
                    disabled={!props.canWrite || submitButtonDisabled}
                    state={
                      officeContext.state.userCardFormSubmitButtonState || SubmitButtonState.Normal
                    }
                    onStateChange={(newState: SubmitButtonState) => {
                      officeContext.setUserCardFormSubmitButtonState(newState);
                    }}
                    normalStateLabel={t(translations.user_card_form.save_button)}
                    loadingStateLabel={t(translations.user_card_form.saving_button)}
                    successStateLabel={t(translations.user_card_form.success.save_success_button)}
                    errorStateLabel={t(translations.user_card_form.errors.error_on_save_button)}
                    endIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                  />
                </Box>
              </Box>
              <Modal
                open={deleteDialogOpened}
                onClose={() => setDeleteDialogOpened(false)}
                className="user-card-modal"
                maxWidth="sm"
                fullWidth
                preventCloseClick
                actions={[
                  {
                    label: t(translations.modal.cancel),
                    type: 'destructive',
                    onClick: () => setDeleteDialogOpened(false),
                  },
                  {
                    label: t(translations.modal.confirm),
                    type: 'nondestructive',
                    onClick: handleDeleteUserCard,
                  },
                ]}
              >
                <Stack>
                  <Typography className="title">
                    {t(translations.user_card_form.delete_dialog_title, {
                      name: originalModel?.name,
                    })}
                  </Typography>
                  <Typography className="subtitle">
                    {t(translations.user_card_form.delete_dialog_content)}
                  </Typography>
                </Stack>
              </Modal>
            </>
          )}
        </Box>
      </Box>
      {/* Router */}
      {model && props.canWrite && (
        <RouterPrompt
          // Show prompt if the data changed but make sure the path
          // is not changing because the card was deleted/created
          when={
            dataChanged &&
            officeContext.state.userCardFormSubmitButtonState !== SubmitButtonState.Loading &&
            officeContext.state.userCardFormDeleteButtonState !== SubmitButtonState.Loading
          }
          subtitle={routerDialogTitle}
          message={routerDialogContent}
          onOK={() => true}
          onCancel={() => false}
          toastNotification={{
            severity: 'info',
            message: routerCancelToast,
          }}
        />
      )}
    </>
  );
};
